import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { MediaData } from "./media";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { Paginated } from "./types";
import { OPMFATValidationSchema } from "./rfs";

interface OPMFATData {
  id: string;
  rfs: {
    id: string;
  };
  valid: boolean;
  poleFat: string;
  imageFat: string;
  outputFat: string;
  splitterFat: string;
  poleFatStatus: boolean;
  imageFatStatus: boolean;
  outputFatStatus: boolean;
  splitterFatStatus: boolean;
  fatId: string;
  outputFatText: string;
  fatIdText: string;
  poleFatMedia: MediaData;
  imageFatMedia: MediaData;
  outputFatMedia: MediaData;
  splitterFatMedia: MediaData;
}

interface InfiniteOPMFATData extends Paginated {
  content: OPMFATData[];
}

/* ============ *
 * READ OPM FAT *
 * ============ */

interface OPMFATQueryKey {
  rfsId?: string;
  opmFdtRfsID?: string;
}
interface InfiniteOPMFATQueryKey extends DataConfig {
  rfsId?: string;
  opmFdtRfsID?: string;
}
async function getOPMFATs(
  queryFunctionContext: QueryFunctionContext,
): Promise<OPMFATData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, opmFATQueryKey] = queryKey;
  const { rfsId, opmFdtRfsID } = opmFATQueryKey as OPMFATQueryKey;

  let params = "?";
  if (rfsId) {
    params += `rfs.id=${rfsId}`;
  }
  if (opmFdtRfsID) {
    params += `&opmFdt.rfs.id=${opmFdtRfsID}`;
  }

  const response = await axiosInstance.get("/api/crud/opm-fat" + params);
  return response.data.data.content;
}
function useOPMFATs(opmFATQueryKey: OPMFATQueryKey) {
  const { rfsId, opmFdtRfsID } = opmFATQueryKey;
  return useQuery({
    queryKey: ["opmFats", { rfsId, opmFdtRfsID }],
    queryFn: getOPMFATs,
  });
}

async function getInfiniteOPMFATs(
  queryFunctionContext: QueryFunctionContext,
): Promise<InfiniteOPMFATData> {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, opmFATQueryKey] = queryKey;
  const { rfsId, opmFdtRfsID, sort, asc } =
    opmFATQueryKey as InfiniteOPMFATQueryKey;

  let params = "?";
  if (rfsId) {
    params += `rfs.id=${rfsId}`;
  }
  if (opmFdtRfsID) {
    params += `&opmFdt.rfs.id=${opmFdtRfsID}`;
  }

  const response = await axiosInstance.get(
    "/api/crud/opm-fat" +
      params +
      `&page=${pageParam}&sort=${sort ?? "fatId"}&asc=${asc ?? "true"}`,
  );
  return response.data.data;
}
function useInfiniteOPMFATs(opmFATQueryKey: InfiniteOPMFATQueryKey) {
  const { rfsId, opmFdtRfsID, sort, asc } = opmFATQueryKey;
  return useInfiniteQuery({
    queryKey: ["opmFats-infinite", { rfsId, opmFdtRfsID, sort, asc }],
    queryFn: getInfiniteOPMFATs,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

interface Data {
  totalElements: number;
}

async function getFATs(
  queryFunctionContext: QueryFunctionContext,
): Promise<Data> {
  const { queryKey } = queryFunctionContext;
  const [_, opmFATQueryKey] = queryKey;
  const { rfsId, opmFdtRfsID } = opmFATQueryKey as OPMFATQueryKey;

  let params = "?";
  if (rfsId) {
    params += `rfs.id=${rfsId}`;
  }
  if (opmFdtRfsID) {
    params += `&opmFdt.rfs.id=${opmFdtRfsID}`;
  }

  const response = await axiosInstance.get("/api/crud/opm-fat" + params);
  return response.data.data;
}
function useFATs(opmFATQueryKey: OPMFATQueryKey) {
  const { rfsId, opmFdtRfsID } = opmFATQueryKey;
  return useQuery({
    queryKey: ["fats", { rfsId, opmFdtRfsID }],
    queryFn: getFATs,
  });
}

/* ===================== *
 * Get Number From Image *
 * ===================== */

interface OPMFATGeneratePayload {
  imageFatMedia: {
    path?: string;
  };
  outputFatMedia: {
    path?: string;
  };
  fatId?: string;
}

interface Generated {
  outputFatText?: string;
  fatIdText?: string;
}

async function getDataFromImage(
  data: Partial<OPMFATGeneratePayload>,
): Promise<Generated> {
  const response = await axiosInstance.post("/api/crud/opm-fat/generate", data);
  return response.data.data;
}
function useOPMFATGenerate() {
  return useMutation({
    mutationFn: (data: Partial<OPMFATGeneratePayload>) =>
      getDataFromImage(data),
  });
}

async function validateOpmFatInReview(
  id: string,
  data: OPMFATValidationSchema,
) {
  const response = await axiosInstance.post(
    `/api/crud/opm-fat/${id}/validate`,
    {
      id: data.opmFat.id,
      outputFatText: data.opmFat.outputFatText,
      fatIdText: data.opmFat.fatIdText,
    },
  );
  return response.data;
}
function useInReviewValidateFAT() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OPMFATValidationSchema }) =>
      validateOpmFatInReview(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["opmFats", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["opmFats-infinite"],
      });
    },
  });
}

export type { OPMFATData, InfiniteOPMFATData };
export {
  useOPMFATs,
  useOPMFATGenerate,
  useFATs,
  useInfiniteOPMFATs,
  useInReviewValidateFAT,
};
